import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Search, X, Filter, Mail, Phone, CheckCircle2, XCircle, Clock, Shuffle, Download as DownloadIcon, ChevronDown, ChevronRight, FileText as PraticheLogoIcon } from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import LoaderProgress from '../components/ui/loader-progress';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Autocomplete from '@mui/material/Autocomplete';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Collapse from '@mui/material/Collapse';
import { useJob } from '../context/JobContext';


function PercentageDonut({ value, color }) {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex'
      }}
    >
      <CircularProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, value))}
        size={56}
        thickness={5}
        sx={{
          color,
          backgroundColor: 'rgba(145, 158, 171, 0.12)',
          borderRadius: '50%'
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          variant="subtitle2"
          component="div"
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {`${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const STATUS_COLORS = {
  created: '#0ea5e9',
  opened: '#3b82f6',
  transition: '#f59e0b',
  terminated: '#ef4444',
  failed: '#b91c1c',
  closed: '#16a34a',
  default: '#6b7280'
};

const STATUS_ICONS = {
  created: Clock,
  opened: Clock,
  transition: Shuffle,
  terminated: XCircle,
  failed: XCircle,
  closed: CheckCircle2,
  default: Clock
};

function getStatusVisuals(status) {
  const key = (status || '').toLowerCase();
  const color = STATUS_COLORS[key] || STATUS_COLORS.default;
  const IconComponent = STATUS_ICONS[key] || STATUS_ICONS.default;
  const label =
    key === 'created'
      ? 'Creata'
      : key === 'opened'
      ? 'Aperta'
      : key === 'transition'
      ? 'In transizione'
      : key === 'terminated'
      ? 'Terminata'
      : key === 'failed'
      ? 'Fallita'
      : key === 'closed'
      ? 'Chiusa'
      : status || 'Senza stato';

  return { color, IconComponent, label };
}

export default function Negotiation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addJob } = useJob();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterIssuer, setFilterIssuer] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterUrgency, setFilterUrgency] = useState('');
  const [filterDescriptionUrgency, setFilterDescriptionUrgency] = useState('');
  const [filterProfessione, setFilterProfessione] = useState('');
  const [filterModalitaChiusura, setFilterModalitaChiusura] = useState('');
  const [filterResponsabileLegale, setFilterResponsabileLegale] = useState('');
  const [filterResponsabileContabile, setFilterResponsabileContabile] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewTab, setViewTab] = useState(0);
  const [expandedCustomers, setExpandedCustomers] = useState({});
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filtersReady, setFiltersReady] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [issuerOptions, setIssuerOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [urgencyOptions, setUrgencyOptions] = useState([]);
  const [descriptionUrgencyOptions, setDescriptionUrgencyOptions] = useState([]);
  const [professioniOptions, setProfessioniOptions] = useState([]);
  const [closureModeOptions, setClosureModeOptions] = useState([]);
  const [responsabiliOptions, setResponsabiliOptions] = useState([]);
  const [statusStats, setStatusStats] = useState([]);
  const [loadingStatusStats, setLoadingStatusStats] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    id: false,
    urgency: true,
    descriptionUrgency: true,
    dataApertura: true,
    dataDecadenza: true,
    dataChiusura: true,
    modalitaChiusura: true,
    responsabileLegale: true,
    responsabileContabile: true,
    createdAt: true
  });
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);

  const buildListQueryParams = () => {
    const params = {};
    if (filterSearch) params.search = filterSearch;
    if (filterStatus) params.status = filterStatus;
    if (filterIssuer) params.issuer = filterIssuer;
    if (filterCategory) params.category = filterCategory;
    if (filterUrgency) params.urgency = filterUrgency;
    if (filterDescriptionUrgency) params.description_urgency = filterDescriptionUrgency;
    if (filterProfessione) params.professione = filterProfessione;
    if (filterModalitaChiusura) params.modalita_chiusura = filterModalitaChiusura;
    if (filterResponsabileLegale) params.responsabile_legale = filterResponsabileLegale;
    if (filterResponsabileContabile) params.responsabile_contabile = filterResponsabileContabile;
    if (filterCustomerId) params.customer_id = filterCustomerId;
    return params;
  };

  const buildStatusStatsQueryParams = () => {
    const params = {};
    if (filterSearch) params.search = filterSearch;
    if (filterIssuer) params.issuer = filterIssuer;
    if (filterCategory) params.category = filterCategory;
    if (filterUrgency) params.urgency = filterUrgency;
    if (filterDescriptionUrgency) params.description_urgency = filterDescriptionUrgency;
    if (filterProfessione) params.professione = filterProfessione;
    if (filterModalitaChiusura) params.modalita_chiusura = filterModalitaChiusura;
    if (filterResponsabileLegale) params.responsabile_legale = filterResponsabileLegale;
    if (filterResponsabileContabile) params.responsabile_contabile = filterResponsabileContabile;
    if (filterCustomerId) params.customer_id = filterCustomerId;
    return params;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order
      };

      Object.assign(params, buildListQueryParams());

      const res = await api.get('/negotiation', { params });
      setList(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching negotiation list:', error);
      toast.error('Errore nel caricamento delle negoziazioni');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusStats = async () => {
    setLoadingStatusStats(true);
    try {
      const params = buildStatusStatsQueryParams();
      const res = await api.get('/negotiation/stats/status', { params });
      setStatusStats((res.data && res.data.items) || []);
    } catch (error) {
      console.error('Error fetching negotiation status stats:', error);
    } finally {
      setLoadingStatusStats(false);
    }
  };

  useEffect(() => {
    if (!filtersReady) return;
    fetchData();
  }, [
    filtersReady,
    page,
    rowsPerPage,
    order,
    orderBy,
    filterSearch,
    filterStatus,
    filterIssuer,
    filterCategory,
    filterUrgency,
    filterDescriptionUrgency,
    filterProfessione,
    filterModalitaChiusura,
    filterResponsabileLegale,
    filterResponsabileContabile,
    filterCustomerId
  ]);

  useEffect(() => {
    if (!filtersReady) return;
    fetchStatusStats();
  }, [
    filtersReady,
    filterSearch,
    filterIssuer,
    filterCategory,
    filterUrgency,
    filterDescriptionUrgency,
    filterProfessione,
    filterModalitaChiusura,
    filterResponsabileLegale,
    filterResponsabileContabile,
    filterCustomerId
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    const urgencyParam = params.get('urgency');
    const statusParam = params.get('status');
    const customerIdParam = params.get('customer_id');
    if (searchParam) {
      setFilterSearch(searchParam);
    }
    if (urgencyParam) {
      setFilterUrgency(urgencyParam);
      setShowFilters(true);
    }
    if (statusParam) {
      setFilterStatus(statusParam);
      setShowFilters(true);
    }
    if (customerIdParam) {
      setFilterCustomerId(customerIdParam);
      setShowFilters(true);
    }
    setFiltersReady(true);
  }, [location.search]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [catRes, issuerRes, usersRes, filtersRes] = await Promise.all([
          api.get('/category-negotiation'),
          api.get('/issuer'),
          api.get('/lorenzo-users'),
          api.get('/negotiation/filters')
        ]);

        setCategoryOptions(
          [...new Set(
            (catRes.data || [])
              .map((item) => item.name)
              .filter(Boolean)
          )].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
        );

        setIssuerOptions(
          [...new Set(
            (issuerRes.data || [])
              .map((item) => item.name)
              .filter(Boolean)
          )].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
        );

        const names = (usersRes.data || [])
          .map((u) => [u.name, u.surname].filter(Boolean).join(' '))
          .filter((n) => n);
        const uniqueNames = [...new Set(names)].sort();
        setResponsabiliOptions(uniqueNames);

        setStatusOptions(
          ((filtersRes.data && filtersRes.data.statuses) || []).filter(Boolean)
        );
        setUrgencyOptions(
          ((filtersRes.data && filtersRes.data.urgencies) || []).filter(Boolean)
        );
        setDescriptionUrgencyOptions(
          ((filtersRes.data && filtersRes.data.descriptionUrgencies) || []).filter(Boolean)
        );
        setProfessioniOptions(
          ((filtersRes.data && filtersRes.data.professioni) || [])
            .filter(Boolean)
            .sort((a, b) => String(a).localeCompare(String(b), 'it', { sensitivity: 'base' }))
        );
        setClosureModeOptions(
          ((filtersRes.data && filtersRes.data.closureModes) || []).filter(Boolean)
        );
      } catch (error) {
        console.error('Error fetching negotiation filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterSearch = (event) => {
    setFilterSearch(event.target.value);
    setPage(0);
  };

  const handleFilterStatus = (event) => {
    setFilterStatus(event.target.value);
    setPage(0);
  };

  const handleFilterIssuer = (event) => {
    setFilterIssuer(event.target.value);
    setPage(0);
  };

  const handleFilterCategory = (event) => {
    setFilterCategory(event.target.value);
    setPage(0);
  };

  const handleFilterUrgency = (event) => {
    setFilterUrgency(event.target.value);
    setPage(0);
  };

  const handleFilterDescriptionUrgency = (event) => {
    setFilterDescriptionUrgency(event.target.value);
    setPage(0);
  };

  const handleFilterModalitaChiusura = (event) => {
    setFilterModalitaChiusura(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setFilterSearch('');
    setFilterStatus('');
    setFilterIssuer('');
    setFilterCategory('');
    setFilterUrgency('');
    setFilterModalitaChiusura('');
    setFilterResponsabileLegale('');
    setFilterResponsabileContabile('');
    setPage(0);
  };

  const activeFiltersCount = [
    filterStatus, 
    filterIssuer, 
    filterCategory,
    filterUrgency,
    filterDescriptionUrgency,
    filterProfessione,
    filterModalitaChiusura,
    filterResponsabileLegale,
    filterResponsabileContabile
  ].filter(Boolean).length;

  const optionalColumnsActive =
    columnVisibility.id ||
    columnVisibility.urgency ||
    columnVisibility.descriptionUrgency ||
    columnVisibility.dataApertura ||
    columnVisibility.dataDecadenza ||
    columnVisibility.dataChiusura ||
    columnVisibility.modalitaChiusura ||
    columnVisibility.responsabileLegale ||
    columnVisibility.responsabileContabile ||
    columnVisibility.createdAt;

  const handleOpenColumnMenu = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const exportColumns = useMemo(() => {
    const cols = [
      ...(columnVisibility.id ? ['id'] : []),
      'reference',
      'cliente',
      'professione',
      'email',
      'telefono',
      'creditore',
      'category',
      ...(columnVisibility.responsabileLegale ? ['responsabile_legale'] : []),
      ...(columnVisibility.responsabileContabile ? ['responsabile_contabile'] : []),
      'status',
      ...(columnVisibility.urgency ? ['urgency'] : []),
      ...(columnVisibility.descriptionUrgency ? ['description_urgency'] : []),
      ...(columnVisibility.dataApertura ? ['data_apertura'] : []),
      ...(columnVisibility.dataDecadenza ? ['data_decadenza'] : []),
      ...(columnVisibility.dataChiusura ? ['data_chiusura'] : []),
      ...(columnVisibility.modalitaChiusura ? ['modalita_chiusura'] : []),
      ...(columnVisibility.createdAt ? ['created_at'] : [])
    ];
    return cols;
  }, [columnVisibility]);

  const exportFiltersSummary = useMemo(() => {
    const items = [];
    if (filterSearch) items.push({ label: 'Ricerca', value: filterSearch });
    if (filterStatus) items.push({ label: 'Status', value: filterStatus });
    if (filterIssuer) items.push({ label: 'Creditore', value: filterIssuer });
    if (filterCategory) items.push({ label: 'Category', value: filterCategory });
    if (filterUrgency) items.push({ label: 'Urgency', value: filterUrgency });
    if (filterDescriptionUrgency) items.push({ label: 'Descr. Urgenza', value: filterDescriptionUrgency });
    if (filterProfessione) items.push({ label: 'Professione', value: filterProfessione });
    if (filterModalitaChiusura) items.push({ label: 'Modalità Chiusura', value: filterModalitaChiusura });
    if (filterResponsabileLegale) items.push({ label: 'Resp. Legale', value: filterResponsabileLegale });
    if (filterResponsabileContabile) items.push({ label: 'Resp. Contabile', value: filterResponsabileContabile });
    if (filterCustomerId) items.push({ label: 'Customer ID', value: filterCustomerId });
    return items;
  }, [
    filterSearch,
    filterStatus,
    filterIssuer,
    filterCategory,
    filterUrgency,
    filterDescriptionUrgency,
    filterProfessione,
    filterModalitaChiusura,
    filterResponsabileLegale,
    filterResponsabileContabile,
    filterCustomerId
  ]);

  const handleOpenExportDialog = () => {
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    if (exporting) return;
    setExportDialogOpen(false);
  };

  const handleConfirmExport = async () => {
    try {
      setExporting(true);
      const payload = {
        ...buildListQueryParams(),
        columns: exportColumns
      };
      const res = await api.post('/negotiation/export', payload);
      if (res.data && res.data.jobId) {
        addJob(res.data.jobId, 'negotiation_csv');
        setExportDialogOpen(false);
      } else {
        toast.error('Impossibile avviare export CSV pratiche');
      }
    } catch (error) {
      console.error('Error starting negotiation CSV export:', error);
      toast.error('Errore nell\'avvio dell\'export CSV pratiche');
    } finally {
      setExporting(false);
    }
  };

  const totalColumns =
    1 + // riferimento
    1 + // cliente
    1 + // professione
    1 + // creditore
    1 + // category
    1 + // status
    (columnVisibility.id ? 1 : 0) +
    (columnVisibility.urgency ? 1 : 0) +
    (columnVisibility.descriptionUrgency ? 1 : 0) +
    (columnVisibility.dataApertura ? 1 : 0) +
    (columnVisibility.dataDecadenza ? 1 : 0) +
    (columnVisibility.dataChiusura ? 1 : 0) +
    (columnVisibility.modalitaChiusura ? 1 : 0) +
    (columnVisibility.responsabileLegale ? 1 : 0) +
    (columnVisibility.responsabileContabile ? 1 : 0) +
    (columnVisibility.createdAt ? 1 : 0);

  const customerGroups = useMemo(() => {
    const groups = new Map();

    (list || []).forEach((item) => {
      const key = String(item.rif_customer || '0');

      const name =
        item.customer_ragione_sociale ||
        ((item.customer_name || item.customer_surname)
          ? `${item.customer_name || ''} ${item.customer_surname || ''}`.trim()
          : (item.rif_customer ? `ID: ${item.rif_customer}` : '-'));

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name,
          professione: item.customer_professione || '',
          email: item.customer_email || '',
          telephone: item.customer_telephone || '',
          items: []
        });
      }

      groups.get(key).items.push(item);

      if (!groups.get(key).email && item.customer_email) {
        groups.get(key).email = item.customer_email;
      }
      if (!groups.get(key).telephone && item.customer_telephone) {
        groups.get(key).telephone = item.customer_telephone;
      }
    });

    return Array.from(groups.values())
      .map((g) => ({
        ...g,
        issuerCount: new Set((g.items || []).map((x) => x.issuer_name).filter(Boolean)).size
      }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'it', { sensitivity: 'base' }));
  }, [list]);

  const toggleCustomerExpanded = (key) => {
    setExpandedCustomers((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PraticheLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Pratiche
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lista pratiche con filtri avanzati, KPI di stato e assegnazioni
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          size="large"
          onClick={handleOpenExportDialog}
          startIcon={<DownloadIcon size={18} />}
          sx={{ textTransform: 'none' }}
        >
          Esporta
        </Button>
      </Box>

      {statusStats.length > 0 && (
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 2,
            overflowX: 'auto',
            pb: 1
          }}
        >
          {statusStats.map((stat) => {
            const visuals = getStatusVisuals(stat.status);
            const IconComponent = visuals.IconComponent;
            return (
              <Card
                key={stat.status || 'no-status'}
                sx={{
                  borderRadius: 3,
                  boxShadow:
                    '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.16)',
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  border:
                    filterStatus === stat.status
                      ? `2px solid ${visuals.color}`
                      : '1px solid rgba(145, 158, 171, 0.24)',
                  flex: '0 0 320px',
                  minWidth: 320,
                  '&:hover': {
                    boxShadow:
                      '0 0 2px 0 rgba(145, 158, 171, 0.32), 0 16px 32px -4px rgba(145, 158, 171, 0.24)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => {
                  setFilterStatus(stat.status || '');
                  setShowFilters(true);
                  setPage(0);
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: `${visuals.color}1A`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <IconComponent size={18} color={visuals.color} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {visuals.label}
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.percentage.toFixed(1)}% delle pratiche
                  </Typography>
                </Box>
                <PercentageDonut value={stat.percentage} color={visuals.color} />
              </Card>
            );
          })}
        </Box>
      )}

      <StyledCard>
        <Box sx={{ px: 2, pt: 1 }}>
          <Tabs
            value={viewTab}
            onChange={(event, newValue) => setViewTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Pratiche" />
            <Tab label="Clienti" />
          </Tabs>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3, p: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Cerca per riferimento, cliente, creditore..."
              value={filterSearch}
              onChange={handleFilterSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} color="#637381" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {filterSearch && (
                        <IconButton size="small" onClick={() => setFilterSearch('')}>
                          <X size={20} />
                        </IconButton>
                      )}
                      <IconButton onClick={() => setShowFilters(!showFilters)}>
                        <Badge badgeContent={activeFiltersCount} color="primary">
                          <FilterAltIcon color={showFilters || activeFiltersCount > 0 ? 'primary' : 'action'} />
                        </Badge>
                      </IconButton>
                      <Tooltip title="Seleziona colonne aggiuntive">
                        <IconButton onClick={handleOpenColumnMenu}>
                          <ViewColumnIcon color={optionalColumnsActive ? 'primary' : 'action'} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {showFilters && (
            <>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={categoryOptions}
                  value={filterCategory || null}
                  onChange={(event, newValue) => {
                    setFilterCategory(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Category" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={handleFilterStatus}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {statusOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={issuerOptions}
                  value={filterIssuer || null}
                  onChange={(event, newValue) => {
                    setFilterIssuer(newValue || '');
                    setPage(0);
                  }}
                  filterOptions={(options, state) =>
                    options.filter((option) =>
                      option.toLowerCase().includes(state.inputValue.toLowerCase())
                    )
                  }
                  renderInput={(params) => <TextField {...params} label="Creditore" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Urgency</InputLabel>
                  <Select
                    value={filterUrgency}
                    label="Urgency"
                    onChange={handleFilterUrgency}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {urgencyOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={descriptionUrgencyOptions}
                  value={filterDescriptionUrgency || null}
                  onChange={(event, newValue) => {
                    setFilterDescriptionUrgency(newValue || '');
                    setPage(0);
                  }}
                  filterOptions={(options, state) =>
                    options.filter((option) =>
                      String(option || '')
                        .toLowerCase()
                        .includes(String(state.inputValue || '').toLowerCase())
                    )
                  }
                  renderInput={(params) => <TextField {...params} label="Descr. Urgenza" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={professioniOptions}
                  value={filterProfessione || null}
                  onChange={(event, newValue) => {
                    setFilterProfessione(newValue || '');
                    setPage(0);
                  }}
                  filterOptions={(options, state) =>
                    options.filter((option) =>
                      String(option || '')
                        .toLowerCase()
                        .includes(String(state.inputValue || '').toLowerCase())
                    )
                  }
                  renderInput={(params) => <TextField {...params} label="Professione" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Modalità Chiusura</InputLabel>
                  <Select
                    value={filterModalitaChiusura}
                    label="Modalità Chiusura"
                    onChange={handleFilterModalitaChiusura}
                  >
                    <MenuItem value=""><em>Tutte</em></MenuItem>
                    {closureModeOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={responsabiliOptions}
                  value={filterResponsabileLegale || null}
                  onChange={(event, newValue) => {
                    setFilterResponsabileLegale(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. Legale" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={responsabiliOptions}
                  value={filterResponsabileContabile || null}
                  onChange={(event, newValue) => {
                    setFilterResponsabileContabile(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. Contabile" />}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>

        <Menu
          anchorEl={columnMenuAnchorEl}
          open={Boolean(columnMenuAnchorEl)}
          onClose={handleCloseColumnMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Colonne aggiuntive
            </Typography>
          </Box>
          <FormGroup sx={{ px: 2, pb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.id}
                  onChange={() => handleToggleColumn('id')}
                />
              }
              label="ID"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.urgency}
                  onChange={() => handleToggleColumn('urgency')}
                />
              }
              label="Urgency"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.descriptionUrgency}
                  onChange={() => handleToggleColumn('descriptionUrgency')}
                />
              }
              label="Descr. Urgenza"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.dataApertura}
                  onChange={() => handleToggleColumn('dataApertura')}
                />
              }
              label="Data Apertura"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.dataDecadenza}
                  onChange={() => handleToggleColumn('dataDecadenza')}
                />
              }
              label="Data Decadenza"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.dataChiusura}
                  onChange={() => handleToggleColumn('dataChiusura')}
                />
              }
              label="Data Chiusura"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.modalitaChiusura}
                  onChange={() => handleToggleColumn('modalitaChiusura')}
                />
              }
              label="Modalità Chiusura"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.responsabileLegale}
                  onChange={() => handleToggleColumn('responsabileLegale')}
                />
              }
              label="Resp. Legale"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.responsabileContabile}
                  onChange={() => handleToggleColumn('responsabileContabile')}
                />
              }
              label="Resp. Contabile"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.createdAt}
                  onChange={() => handleToggleColumn('createdAt')}
                />
              }
              label="Creato il"
            />
          </FormGroup>
        </Menu>

        {viewTab === 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                {columnVisibility.id && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'reference'}
                    direction={orderBy === 'reference' ? order : 'asc'}
                    onClick={() => handleRequestSort('reference')}
                  >
                    Riferimento
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'customer_name'}
                    direction={orderBy === 'customer_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('customer_name')}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'customer_professione'}
                    direction={orderBy === 'customer_professione' ? order : 'asc'}
                    onClick={() => handleRequestSort('customer_professione')}
                  >
                    Professione
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'issuer_name'}
                    direction={orderBy === 'issuer_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('issuer_name')}
                  >
                    Creditore
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'category_name'}
                    direction={orderBy === 'category_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('category_name')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.responsabileLegale && (
                  <TableCell>Resp. Legale</TableCell>
                )}
                {columnVisibility.responsabileContabile && (
                  <TableCell>Resp. Contabile</TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.urgency && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'urgency'}
                      direction={orderBy === 'urgency' ? order : 'asc'}
                      onClick={() => handleRequestSort('urgency')}
                    >
                      Urgency
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.descriptionUrgency && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'description_urgency'}
                      direction={orderBy === 'description_urgency' ? order : 'asc'}
                      onClick={() => handleRequestSort('description_urgency')}
                    >
                      Descr. Urgenza
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataApertura && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_apertura'}
                      direction={orderBy === 'data_apertura' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_apertura')}
                    >
                      Data Apertura
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataDecadenza && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_decadenza'}
                      direction={orderBy === 'data_decadenza' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_decadenza')}
                    >
                      Data Decadenza
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataChiusura && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_chiusura'}
                      direction={orderBy === 'data_chiusura' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_chiusura')}
                    >
                      Data Chiusura
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.modalitaChiusura && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'modalita_chiusura'}
                      direction={orderBy === 'modalita_chiusura' ? order : 'asc'}
                      onClick={() => handleRequestSort('modalita_chiusura')}
                    >
                      Modalità Chiusura
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.createdAt && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'created_at'}
                      direction={orderBy === 'created_at' ? order : 'asc'}
                      onClick={() => handleRequestSort('created_at')}
                    >
                      Creato il
                    </TableSortLabel>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 3 }}>
                    <LoaderProgress size={50} />
                  </TableCell>
                </TableRow>
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessuna negoziazione trovata
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                list.map((item) => (
                    <TableRow 
                      key={item.id} 
                      hover 
                      onClick={() => navigate(`/negotiation/${item.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {columnVisibility.id && <TableCell>{item.id}</TableCell>}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {item.reference}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {item.customer_ragione_sociale || 
                             ((item.customer_name || item.customer_surname) ? 
                               `${item.customer_name || ''} ${item.customer_surname || ''}`.trim() : 
                               (item.rif_customer ? `ID: ${item.rif_customer}` : '-'))}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                            {item.customer_email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                                <Mail size={12} />
                                {item.customer_email}
                              </Box>
                            )}
                            {item.customer_telephone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                                <Phone size={12} />
                                {item.customer_telephone}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{item.customer_professione || '-'}</TableCell>
                      <TableCell>{item.issuer_name || '-'}</TableCell>
                      <TableCell>{item.category_name || '-'}</TableCell>
                      {columnVisibility.responsabileLegale && (
                        <TableCell>{item.responsabile_legale_name || '-'}</TableCell>
                      )}
                      {columnVisibility.responsabileContabile && (
                        <TableCell>{item.responsabile_contabile_name || '-'}</TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={item.status}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      {columnVisibility.urgency && (
                        <TableCell>
                          {item.urgency && (
                            <Chip 
                              label={item.urgency} 
                              size="small" 
                              color={item.urgency === 'high' ? 'error' : 'default'}
                            />
                          )}
                        </TableCell>
                      )}
                      {columnVisibility.descriptionUrgency && (
                        <TableCell>{item.description_urgency || '-'}</TableCell>
                      )}
                      {columnVisibility.dataApertura && (
                        <TableCell>
                          {item.data_apertura ? new Date(item.data_apertura).toLocaleDateString() : '-'}
                        </TableCell>
                      )}
                      {columnVisibility.dataDecadenza && (
                        <TableCell>
                          {item.data_decadenza ? new Date(item.data_decadenza).toLocaleDateString() : '-'}
                        </TableCell>
                      )}
                      {columnVisibility.dataChiusura && (
                        <TableCell>
                          {item.data_chiusura ? new Date(item.data_chiusura).toLocaleDateString() : '-'}
                        </TableCell>
                      )}
                      {columnVisibility.modalitaChiusura && (
                        <TableCell>{item.modalita_chiusura || '-'}</TableCell>
                      )}
                      {columnVisibility.createdAt && (
                        <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                      )}
                    </TableRow>
                  ))
              )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 56 }} />
                  <TableCell>Cliente</TableCell>
                  <TableCell align="right">Pratiche</TableCell>
                  <TableCell align="right">Creditori</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <LoaderProgress size={50} />
                    </TableCell>
                  </TableRow>
                ) : customerGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nessun cliente trovato
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customerGroups.map((group) => {
                    const expanded = Boolean(expandedCustomers[group.key]);
                    return (
                      <React.Fragment key={group.key}>
                        <TableRow hover sx={{ cursor: 'default' }}>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCustomerExpanded(group.key);
                              }}
                            >
                              {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {group.name}
                              </Typography>
                              {group.professione && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {group.professione}
                                </Typography>
                              )}
                              {(group.email || group.telephone) && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                  {group.email && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                                      <Mail size={12} />
                                      {group.email}
                                    </Box>
                                  )}
                                  {group.telephone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                                      <Phone size={12} />
                                      {group.telephone}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{group.items.length}</TableCell>
                          <TableCell align="right">{group.issuerCount}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 0, borderBottom: expanded ? 'none' : undefined }}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Riferimento</TableCell>
                                      <TableCell>Creditore</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Urgency</TableCell>
                                      <TableCell>Data Apertura</TableCell>
                                      <TableCell>Data Decadenza</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {group.items.map((item) => (
                                      <TableRow
                                        key={item.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/negotiation/${item.id}`)}
                                      >
                                        <TableCell>{item.reference}</TableCell>
                                        <TableCell>{item.issuer_name || '-'}</TableCell>
                                        <TableCell>
                                          <Chip label={item.status} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>{item.urgency || '-'}</TableCell>
                                        <TableCell>
                                          {item.data_apertura ? new Date(item.data_apertura).toLocaleDateString() : '-'}
                                        </TableCell>
                                        <TableCell>
                                          {item.data_decadenza ? new Date(item.data_decadenza).toLocaleDateString() : '-'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog open={exportDialogOpen} onClose={handleCloseExportDialog} fullWidth maxWidth="sm">
        <DialogTitle>Esporta CSV</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Genera un file CSV con tutte le pratiche che rispettano i filtri correnti. Il file include le colonne attualmente visibili in lista.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Filtri utilizzati
            </Typography>

            {exportFiltersSummary.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessun filtro attivo. Verranno esportate tutte le pratiche.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {exportFiltersSummary.map((f) => (
                  <Chip key={f.label} label={`${f.label}: ${f.value}`} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExportDialog} disabled={exporting} sx={{ textTransform: 'none' }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmExport}
            disabled={exporting}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: 'rgb(33, 43, 54)' }}
          >
            {exporting ? 'Avvio in corso...' : 'Conferma esportazione'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
